// InlineSearch · expanding search field for the top utility bar.
//
// Replaces the modal-style GlobalSearch trigger so the search doesn't
// feel like a popup (some users worry popup blockers may interfere).
// Instead, the search lives inline in the top-tier of the navbar:
//
//   collapsed → small icon button
//   expanded  → 320px input + anchored dropdown panel of results
//               directly below the input
//
// Result rendering, scoring + keyboard navigation are unchanged from
// GlobalSearch · we reuse `searchRecords()` and the same row layout
// so users get an identical search experience.
//
// Keyboard:
//   ⌘K / Ctrl+K  → expand + focus (wired from Navbar)
//   /            → expand + focus (wired from Navbar)
//   ESC          → collapse
//   ↑↓ + Enter   → navigate / open result

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, X, ArrowRight, Hash, CornerDownLeft, Command } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import { searchRecords } from "@/lib/searchIndex";

const EXPANDED_WIDTH_DESKTOP = 320;
const EXPANDED_WIDTH_MIN     = 200;  // floor for ultra-narrow viewports
const COLLAPSED_WIDTH        = 36;

const QUICK_QUERIES_FALLBACK = [
  "Live spreads", "EURUSD", "Gold", "Bitcoin",
  "Bonus", "Leverage", "Withdrawals", "MT5",
];

function ResultRow({ rec, active, onSelect }) {
  const Icon = rec.Icon || Search;
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      data-testid={`search-result-${rec.kind}-${rec.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").substring(0, 40)}`}
      onMouseDown={(e) => { e.preventDefault(); onSelect(rec); }}
      className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors ${
        active
          ? "bg-[color:var(--brand-soft,color-mix(in_srgb,var(--brand)_12%,transparent))]"
          : "hover:bg-[color:var(--surface-2)]"
      }`}
      style={ active ? { boxShadow: "inset 0 0 0 1px color-mix(in srgb, var(--brand) 35%, transparent)" } : undefined }
    >
      <span
        className="shrink-0 w-7 h-7 rounded-md grid place-items-center"
        style={{
          background: "color-mix(in srgb, var(--brand) 10%, transparent)",
          border: "1px solid color-mix(in srgb, var(--brand) 22%, transparent)",
          color: "var(--brand)",
        }}
      >
        <Icon size={13} strokeWidth={1.8} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[12.5px] font-medium text-[color:var(--text-1)] leading-tight truncate">
          {rec.title}
        </span>
        {rec.subtitle && (
          <span className="block text-[12px] text-[color:var(--text-3)] leading-tight mt-0.5 truncate font-light">
            {rec.subtitle}
          </span>
        )}
      </span>
      {rec.badge && (
        <span className="font-mono text-[12px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded text-[color:var(--text-3)] shrink-0"
              style={{ background: "color-mix(in srgb, var(--text-3) 12%, transparent)" }}>
          {rec.badge}
        </span>
      )}
      <ArrowRight size={12} className={`shrink-0 transition-opacity ${active ? "opacity-100 text-[color:var(--brand)]" : "opacity-0"}`} />
    </button>
  );
}

export default function InlineSearch({ openSignal, testIdSuffix = "", onExpandedChange }) {
  const { t } = useTranslation();
  // openSignal is bumped by the parent when the user presses ⌘K · we
  // expand + focus on any change. A simple incrementing counter avoids
  // having to maintain a controlled `open` prop.
  const navigate = useNavigate();
  const location = useLocation();
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [q, setQ] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  // Anchor coordinates for the portalised dropdown · recomputed on
  // open, scroll and resize so the panel always sits directly under
  // the search input (the dropdown is rendered into document.body so
  // it can't be clipped by overflow-hidden in the navbar's top tier).
  const [anchor, setAnchor] = useState(null);
  // Effective expanded pill width. Re-measured whenever the wrapper
  // might shift (resize / scroll). Capped so the pill itself never
  // overflows the viewport on narrow screens.
  const [expandedWidth, setExpandedWidth] = useState(EXPANDED_WIDTH_DESKTOP);
  // Mobile mode · on <640px viewports we keep the pill at icon size
  // and move the actual input into the dropdown so it never crowds
  // the navbar logo / nav buttons.
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);
  const mobileInputRef = useRef(null);

  const results = useMemo(() => searchRecords(q, { limit: 16 }), [q]);
  const grouped = useMemo(() => {
    const m = new Map();
    results.forEach((r) => {
      if (!m.has(r.group)) m.set(r.group, []);
      m.get(r.group).push(r);
    });
    return [...m.entries()];
  }, [results]);

  // Notify parent so it can lock the top tier open while searching.
  useEffect(() => {
    onExpandedChange?.(expanded);
  }, [expanded, onExpandedChange]);

  // Compute / refresh the dropdown anchor whenever expanded.
  // On narrow viewports (<640px) we anchor to both edges with an 8px
  // gutter so the dropdown never bleeds off-screen even if the
  // expanded input itself happens to overflow the right edge (which
  // can push window.innerWidth - r.right negative). On wider viewports
  // we keep the original right-anchored behaviour so the dropdown sits
  // tucked under the input.
  //
  // The same measurement also computes the effective expanded pill
  // width — we cap it at (viewport - wrapper.left - 8px gutter) so the
  // input itself never extends past the viewport edge on mobile.
  useEffect(() => {
    const place = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const mobile = vw < 640;
      setIsMobile(mobile);

      // Effective expanded width · the pill is anchored at its left
      // edge (because the toggle button is the first child), so the
      // available width to grow into is `viewport - left - gutter`.
      // We clamp between MIN and DESKTOP so the pill stays usable.
      const gutter = 12;
      const available = vw - r.left - gutter;
      const target = Math.min(EXPANDED_WIDTH_DESKTOP, Math.max(EXPANDED_WIDTH_MIN, available));
      setExpandedWidth(target);

      if (!expanded) return;
      if (mobile) {
        setAnchor({ top: r.bottom + 8, left: 8, right: 8, mobile: true });
      } else {
        // Clamp at 8px so we never produce a negative right offset
        // even if the input briefly overflows the viewport during the
        // expand animation.
        const distFromRight = Math.max(8, vw - r.right);
        setAnchor({ top: r.bottom + 8, right: distFromRight, mobile: false });
      }
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [expanded]);

  // Expose expand() to parent via openSignal.
  useEffect(() => {
    if (openSignal > 0) {
      setExpanded(true);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [openSignal]);

  // Outside click + ESC collapses.
  useEffect(() => {
    if (!expanded) return;
    const onDown = (e) => {
      if (!wrapperRef.current?.contains(e.target)) {
        setExpanded(false);
        setQ("");
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setExpanded(false);
        setQ("");
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [expanded]);

  // Collapse on route change so the dropdown doesn't linger across pages.
  useEffect(() => {
    setExpanded(false);
    setQ("");
  }, [location.pathname]);

  // Reset active index when results change.
  useEffect(() => { setActiveIdx(0); }, [q]);

  const commit = useCallback((rec) => {
    if (!rec) return;
    setExpanded(false);
    setQ("");
    if (rec.to?.startsWith("http")) {
      window.open(rec.to, "_blank", "noopener,noreferrer");
    } else {
      navigate(rec.to);
    }
  }, [navigate]);

  const onInputKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[activeIdx]) commit(results[activeIdx]);
    }
  };

  // Scroll active row into view inside the dropdown.
  useEffect(() => {
    if (!expanded || !listRef.current) return;
    const el = listRef.current.querySelector('[aria-selected="true"]');
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIdx, expanded]);

  // Flat index counter so each rendered row knows its position in the
  // ↑/↓ keyboard nav stream even though they're grouped visually.
  let flatCursor = 0;

  return (
    <div
      ref={wrapperRef}
      className="relative shrink-0"
      data-testid={`inline-search${testIdSuffix ? "-" + testIdSuffix : ""}`}
    >
      {/* Pill · either the small icon button (collapsed) or the
          actual text input (expanded). On mobile (<640px) the pill
          NEVER expands · it stays at icon size and the actual input
          lives inside the dropdown panel below, so the navbar logo
          and nav buttons never get pushed away by an expanding pill. */}
      <motion.div
        initial={false}
        animate={{ width: expanded && !isMobile ? expandedWidth : COLLAPSED_WIDTH }}
        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
        className="h-8 rounded-full flex items-center overflow-hidden border max-w-full"
        style={{
          background: expanded
            ? "var(--surface)"
            : "color-mix(in srgb, var(--surface) 60%, transparent)",
          borderColor: expanded
            ? "color-mix(in srgb, var(--brand) 35%, var(--line-strong))"
            : "var(--line-strong)",
          boxShadow: expanded && !isMobile
            ? "0 6px 16px -10px color-mix(in srgb, var(--brand) 40%, transparent), inset 0 0 0 1px color-mix(in srgb, var(--brand) 10%, transparent)"
            : "none",
        }}
      >
        <button
          type="button"
          onClick={() => {
            if (expanded) {
              if (isMobile) {
                // On mobile the icon doubles as the close-button while
                // the dropdown is open.
                setExpanded(false);
                setQ("");
              } else {
                // Desktop: click-the-icon-while-open clears the query
                // rather than collapsing · a quick reset affordance.
                setQ("");
                inputRef.current?.focus();
              }
            } else {
              setExpanded(true);
              // Focus the inline input on desktop, the dropdown input on mobile.
              setTimeout(() => {
                if (isMobile) mobileInputRef.current?.focus();
                else inputRef.current?.focus();
              }, 60);
            }
          }}
          aria-label={expanded && isMobile ? t("comp.search.closeAria", "Close search") : t("comp.search.openAria", "Open site search")}
          data-testid="inline-search-toggle"
          className="h-8 w-9 shrink-0 grid place-items-center text-[color:var(--text-3)] hover:text-[color:var(--text-1)] transition-colors"
        >
          {expanded && isMobile ? <X size={13} strokeWidth={2} /> : <Search size={13} strokeWidth={2} />}
        </button>
        {/* Desktop-only inline input. On mobile we render the input
            inside the dropdown panel below instead. */}
        {!isMobile && (
          <>
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={() => setExpanded(true)}
              onKeyDown={onInputKeyDown}
              placeholder={t("comp.search.searchPlaceholder")}
              className={`flex-1 min-w-0 bg-transparent outline-none border-0 text-[12.5px] placeholder:text-[color:var(--text-3)] text-[color:var(--text-1)] pr-2 transition-opacity ${
                expanded ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={expanded}
              aria-controls="inline-search-listbox"
              data-testid="inline-search-input"
              spellCheck={false}
              autoComplete="off"
              tabIndex={expanded ? 0 : -1}
            />
            {expanded && (
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); setExpanded(false); setQ(""); }}
                aria-label={t("comp.search.closeAria", "Close search")}
                data-testid="inline-search-close"
                className="shrink-0 h-8 w-7 grid place-items-center text-[color:var(--text-3)] hover:text-[color:var(--text-1)] transition-colors"
              >
                <X size={12} />
              </button>
            )}
          </>
        )}
      </motion.div>

      {/* Anchored dropdown · rendered via portal into document.body so
          it can't be clipped by overflow-hidden on the navbar's top
          tier (which collapses on scroll). We track the anchor coords
          on every scroll/resize so the panel always sits directly
          under the search pill. */}
      {anchor && createPortal(
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.14 }}
              id="inline-search-listbox"
              role="listbox"
              className="fixed rounded-xl overflow-hidden z-[100]"
              style={anchor.mobile ? {
                top: anchor.top,
                left: anchor.left,
                right: anchor.right,
                background: "var(--surface)",
                border: "1px solid color-mix(in srgb, var(--brand) 18%, var(--line-strong))",
                boxShadow:
                  "0 22px 50px -18px rgba(0,0,0,0.35), 0 10px 22px -10px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.04)",
              } : {
                top: anchor.top,
                right: anchor.right,
                width: 380,
                maxWidth: "92vw",
                background: "var(--surface)",
                border: "1px solid color-mix(in srgb, var(--brand) 18%, var(--line-strong))",
                boxShadow:
                  "0 22px 50px -18px rgba(0,0,0,0.35), 0 10px 22px -10px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.04)",
              }}
              data-testid="inline-search-dropdown"
              onMouseDown={(e) => e.stopPropagation()}
            >
            {/* Mobile-only inline input at the top of the dropdown.
                On mobile the navbar pill stays icon-only · the actual
                typing happens here so the layout never gets pushed. */}
            {anchor.mobile && (
              <div
                className="flex items-center gap-2 px-3 py-2.5 border-b"
                style={{ borderColor: "var(--line)", background: "color-mix(in srgb, var(--brand) 3%, var(--surface))" }}
              >
                <Search size={14} strokeWidth={2} className="shrink-0 text-[color:var(--brand)]" />
                <input
                  ref={mobileInputRef}
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={onInputKeyDown}
                  placeholder={t("comp.search.searchPlaceholder")}
                  className="flex-1 min-w-0 bg-transparent outline-none border-0 text-[14px] placeholder:text-[color:var(--text-3)] text-[color:var(--text-1)]"
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={expanded}
                  aria-controls="inline-search-listbox"
                  data-testid="inline-search-input-mobile"
                  spellCheck={false}
                  autoComplete="off"
                />
                {q && (
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); setQ(""); mobileInputRef.current?.focus(); }}
                    aria-label={t("comp.search.clearAria", "Clear search")}
                    data-testid="inline-search-clear-mobile"
                    className="shrink-0 h-6 w-6 grid place-items-center rounded-full text-[color:var(--text-3)] hover:text-[color:var(--text-1)] transition-colors"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            )}
            <div ref={listRef} className="max-h-[55vh] overflow-y-auto p-2">
              {!q && (
                <div className="px-1 py-1" data-testid="inline-search-quick">
                  <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--text-3)] mb-2 px-1.5">
                    {t("comp.search.quickSearches", "Quick searches")}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(t("comp.search.quickQueries", { returnObjects: true, defaultValue: QUICK_QUERIES_FALLBACK }) || QUICK_QUERIES_FALLBACK).map((qq, i) => {
                      // The query value passed to the search engine must
                      // stay in the source locale (English) since the
                      // search index is built from English page titles.
                      // We display the localized label but search using
                      // the English fallback at the same index.
                      const queryValue = QUICK_QUERIES_FALLBACK[i] || qq;
                      return (
                      <button
                        key={qq}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setQ(queryValue);
                          (isMobile ? mobileInputRef : inputRef).current?.focus();
                        }}
                        data-testid={`inline-search-quick-${queryValue.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] transition-colors"
                        style={{
                          background: "color-mix(in srgb, var(--brand) 8%, transparent)",
                          border: "1px solid color-mix(in srgb, var(--brand) 22%, transparent)",
                          color: "var(--text-2)",
                        }}
                      >
                        <Hash size={9} className="text-[color:var(--brand)]" />
                        {qq}
                      </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {q && results.length === 0 && (
                <div className="px-3 py-7 text-center" data-testid="inline-search-empty">
                  <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[color:var(--text-3)] mb-1.5">
                    {t("comp.search.noResults", "No results")}
                  </div>
                  <div className="text-[12px] text-[color:var(--text-2)] leading-relaxed font-light">
                    <Trans
                      i18nKey="comp.search.tryHint"
                      defaults="Try a symbol like <s>EURUSD</s> or a topic like <s>leverage</s>."
                      values={{ symbol: "EURUSD", topic: "leverage" }}
                      components={{ s: <span className="font-mono text-[color:var(--text-1)]" /> }}
                    />
                  </div>
                </div>
              )}

              {q && results.length > 0 && grouped.map(([group, items]) => (
                <div key={group} className="mb-1.5 last:mb-0" data-testid={`inline-search-group-${group.toLowerCase()}`}>
                  <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--text-3)] px-2 pt-1 pb-1">
                    {t(`comp.search.groups.${group}`, group)}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    {items.map((rec) => {
                      const myIdx = flatCursor++;
                      return (
                        <div key={`${rec.to}-${rec.title}`}>
                          <ResultRow
                            rec={rec}
                            active={myIdx === activeIdx}
                            onSelect={commit}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div
              className="flex items-center justify-between gap-3 px-3 h-8 border-t font-mono text-[12px] uppercase tracking-[0.18em] text-[color:var(--text-3)]"
              style={{ borderColor: "var(--line)", background: "color-mix(in srgb, var(--surface-2) 60%, transparent)" }}
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center gap-1">
                  <kbd className="font-sans rounded px-1 text-[12px] border" style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }}>↑↓</kbd>
                  {t("comp.search.move", "Move")}
                </span>
                <span className="inline-flex items-center gap-1">
                  <kbd className="font-sans rounded px-1 text-[12px] border inline-flex items-center" style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }}><CornerDownLeft size={8} /></kbd>
                  {t("comp.search.openKey", "Open")}
                </span>
              </div>
              <span className="inline-flex items-center gap-1">
                <kbd className="font-sans rounded px-1 text-[12px] border inline-flex items-center" style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }}>
                  <Command size={8} className="mr-0.5" />K
                </kbd>
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body
      )}
    </div>
  );
}
